import { useEffect, useState, type FormEvent } from "react";
import { AUTH_FAIL_EVENT, checkToken } from "../lib/contentApi";

/**
 * 🔑 **열쇠 다시 넣기** — 저장이 401 로 막혔을 때 화면 위에 뜬다.
 *
 * ## 실제 사고(2026-08-20)
 * 운영자가 벽을 다 고치고 「웹에 올리기」를 눌렀더니 "토큰이 올바르지 않아요(401)" 만 떴다.
 * 서버·경로·CORS 는 전부 정상이었고, **오래 열어 둔 탭이 옛 열쇠를 들고 있던 것**이 원인이다
 * (열쇠는 sessionStorage 라 탭마다 따로 산다 — 다른 탭에서 새 열쇠로 들어가도 이 탭은 옛것 그대로).
 *
 * 그때 고칠 방법이 **새로고침밖에 없었다는 게 진짜 문제**였다. 새로고침하면 방금 고친 벽이 날아간다.
 * 그래서 이 창은 화면을 **그대로 둔 채** 열쇠만 바꿔 끼운다 — 닫으면 고친 내용을 들고
 * 곧바로 다시 「웹에 올리기」를 누를 수 있다.
 *
 * 새 열쇠는 넣기 전에 서버에 확인한다(불변식 7 — 밖에서 온 값을 그냥 믿지 않는다).
 * 여기서 확인하지 않으면 틀린 열쇠를 또 저장하고, 운영자는 같은 401 을 한 번 더 만난다.
 */
export function ReloginModal() {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onFail = () => {
      setErr(null);
      setV("");
      setOpen(true);
    };
    window.addEventListener(AUTH_FAIL_EVENT, onFail);
    return () => window.removeEventListener(AUTH_FAIL_EVENT, onFail);
  }, []);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = v.trim();
    if (!t || busy) return;
    setBusy(true);
    setErr(null);
    const result = await checkToken(t);
    setBusy(false);
    if (result === "bad") {
      setErr("이 열쇠도 안 맞아요. 다른 관리자 창에 들어갈 때 쓴 열쇠를 그대로 넣어 주세요.");
      return;
    }
    if (result === "busy") {
      setErr("잠깐 너무 많이 시도했어요. 1분 뒤에 다시 눌러 주세요(고친 내용은 그대로 있어요).");
      return;
    }
    if (result === "offline") {
      setErr("서버에 연결이 안 돼요. 잠시 뒤 다시 눌러 주세요(고친 내용은 그대로 있어요).");
      return;
    }
    sessionStorage.setItem("gc_admin_token", t);
    setOpen(false);
  }

  return (
    <div className="relogin" role="dialog" aria-modal="true" aria-labelledby="relogin-title">
      <div className="relogin__card">
        <h2 id="relogin-title">🔑 열쇠를 다시 넣어 주세요</h2>
        <p>
          이 창의 열쇠가 오래돼서 저장이 막혔어요. <b>고친 내용은 그대로 있어요</b> — 열쇠를 넣고
          다시 「웹에 올리기」를 누르면 저장돼요.
        </p>
        <form onSubmit={(e) => void submit(e)}>
          <input
            type="password"
            value={v}
            onChange={(e) => setV(e.target.value)}
            placeholder="비밀 열쇠(토큰)"
            autoFocus
          />
          <div className="relogin__row">
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              나중에
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? "확인 중…" : "넣기"}
            </button>
          </div>
        </form>
        {err ? <p className="relogin__err">{err}</p> : null}
      </div>
    </div>
  );
}
